/**
 * The funnel-icon column filter — Excel's AutoFilter, minus the part of AutoFilter that made it
 * dangerous: nothing here is remembered between visits (see useColumnFilters.ts).
 *
 * Lives inside a `SortableHeader`'s `filter` slot. Every click inside the popover stops
 * propagation so it never toggles the header's sort or interferes with a column resize — the
 * same guard `ColumnResizeHandle` uses for the same reason.
 */
import { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import type { ColumnFilterState } from '../../hooks/useColumnFilters';

interface Labels {
    search: string;
    all: string;
    none: string;
    from: string;
    to: string;
    empty: string;
}

interface Props {
    label: string;
    values: string[];
    state: ColumnFilterState | undefined;
    onChange: (next: ColumnFilterState | null) => void;
    numeric?: boolean;
    active: boolean;
    labels: Labels;
}

export function ColumnFilter({ label, values, state, onChange, numeric, active, labels }: Props) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selected = state?.selected ?? null;
    // null means "everything checked" - materialise it against the current value list only
    // when a box actually needs toggling, so an untouched filter never drifts into a stored
    // set that then falls behind a newly-loaded value.
    const effectiveSelected = selected ?? new Set(values);
    const shown = search ? values.filter(v => v.toLowerCase().includes(search.toLowerCase())) : values;

    const commit = (sel: Set<string> | null, min?: number | null, max?: number | null) => {
        const noMin = min === null || min === undefined;
        const noMax = max === null || max === undefined;
        if (sel === null && noMin && noMax) onChange(null);
        else onChange({ selected: sel, min: noMin ? null : min, max: noMax ? null : max });
    };

    const toggleValue = (v: string) => {
        const next = new Set(effectiveSelected);
        if (next.has(v)) next.delete(v); else next.add(v);
        commit(next.size === values.length ? null : next, state?.min, state?.max);
    };

    return (
        <span className="relative inline-block normal-case font-normal" ref={ref}
              onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                title={label}
                className={`inline-flex p-0.5 rounded hover:bg-gray-200 ${active ? 'text-blue-600' : 'text-gray-400'}`}>
                <Filter className="h-3 w-3" fill={active ? 'currentColor' : 'none'} />
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1 z-30 w-56 bg-white border rounded-lg shadow-xl
                                 text-gray-700 p-2 cursor-default">
                    {numeric && (
                        <div className="flex items-center gap-1 mb-2 text-xs">
                            <input type="number" placeholder={labels.from}
                                   aria-label={`${label} ${labels.from}`}
                                   value={state?.min ?? ''}
                                   onChange={e => commit(selected, e.target.value === '' ? null : Number(e.target.value), state?.max)}
                                   className="w-full border rounded px-1 py-0.5" />
                            <span className="text-gray-400">–</span>
                            <input type="number" placeholder={labels.to}
                                   aria-label={`${label} ${labels.to}`}
                                   value={state?.max ?? ''}
                                   onChange={e => commit(selected, state?.min, e.target.value === '' ? null : Number(e.target.value))}
                                   className="w-full border rounded px-1 py-0.5" />
                        </div>
                    )}
                    <input type="text" placeholder={labels.search} value={search}
                           aria-label={`${labels.search.replace(/[.…]+$/, '')} ${label}`}
                           onChange={e => setSearch(e.target.value)}
                           className="w-full border rounded px-1.5 py-1 text-xs mb-1.5" />
                    <div className="flex items-center justify-between text-xs text-blue-600 mb-1 px-0.5">
                        <button type="button" onClick={() => commit(null, state?.min, state?.max)}>{labels.all}</button>
                        <button type="button" onClick={() => commit(new Set(), state?.min, state?.max)}>{labels.none}</button>
                    </div>
                    <div className="max-h-48 overflow-auto border-t pt-1">
                        {shown.length === 0 && <div className="text-xs text-gray-400 px-1 py-2">—</div>}
                        {shown.map(v => (
                            <label key={v}
                                   className="flex items-center gap-1.5 px-1 py-0.5 text-xs hover:bg-gray-50 rounded cursor-pointer">
                                <input type="checkbox" checked={effectiveSelected.has(v)}
                                       onChange={() => toggleValue(v)} />
                                <span className="truncate">{v === '' ? labels.empty : v}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </span>
    );
}
